<script lang="ts">
  import Grid from '@components/grid.svelte'
  import type {
    Colors,
    GameraGridRow,
    GameraTeamReference,
  } from '@statmuse/core/gamera'
  import {
    playerStats,
    players,
    plays,
    periodClock,
    selectedId,
  } from './stores'

  export let teamKey: 'away' | 'home'
  export let team: GameraTeamReference | undefined
  export let live = false

  const teamIdKey =
    teamKey === 'away' ? 'awayOnIcePlayerIds' : 'homeOnIcePlayerIds'

  const forwardColumns = [
    {
      title: 'Forwards',
      type: 'string',
      rowItemKey: 'NAME',
    },
    {
      title: 'G',
      type: 'number',
      rowItemKey: 'G',
    },
    {
      title: 'A',
      type: 'number',
      rowItemKey: 'A',
    },
    {
      title: 'P',
      type: 'number',
      rowItemKey: 'P',
    },
    {
      title: '+/-',
      type: 'number',
      rowItemKey: '+/-',
    },
    {
      title: 'PIM',
      type: 'number',
      rowItemKey: 'PIM',
    },
    {
      title: 'PPG',
      type: 'number',
      rowItemKey: 'PPG',
    },
    {
      title: 'PPA',
      type: 'number',
      rowItemKey: 'PPA',
    },
    {
      title: 'SHG',
      type: 'number',
      rowItemKey: 'SHG',
    },
    {
      title: 'SHA',
      type: 'number',
      rowItemKey: 'SHA',
    },
    {
      title: 'S',
      type: 'number',
      rowItemKey: 'S',
    },
    {
      title: 'S%',
      type: 'number',
      rowItemKey: 'S%',
    },
    {
      title: 'FOW',
      type: 'number',
      rowItemKey: 'FOW',
    },
    {
      title: 'FO',
      type: 'number',
      rowItemKey: 'FO',
    },
    {
      title: 'FOW%',
      type: 'number',
      rowItemKey: 'FOW%',
    },
    {
      title: 'HIT',
      type: 'number',
      rowItemKey: 'HIT',
    },
    {
      title: 'BKS',
      type: 'number',
      rowItemKey: 'BKS',
    },
    {
      title: 'TKA',
      type: 'number',
      rowItemKey: 'TKA',
    },
    {
      title: 'GVA',
      type: 'number',
      rowItemKey: 'GVA',
    },
    {
      title: 'TOI',
      type: 'number',
      rowItemKey: 'TOI',
    },
  ]

  const defenderColumns = [
    {
      title: 'Defensemen',
      type: 'string',
      rowItemKey: 'NAME',
    },
    {
      title: 'G',
      type: 'number',
      rowItemKey: 'G',
    },
    {
      title: 'A',
      type: 'number',
      rowItemKey: 'A',
    },
    {
      title: 'P',
      type: 'number',
      rowItemKey: 'P',
    },
    {
      title: '+/-',
      type: 'number',
      rowItemKey: '+/-',
    },
    {
      title: 'PIM',
      type: 'number',
      rowItemKey: 'PIM',
    },
    {
      title: 'PPG',
      type: 'number',
      rowItemKey: 'PPG',
    },
    {
      title: 'PPA',
      type: 'number',
      rowItemKey: 'PPA',
    },
    {
      title: 'SHG',
      type: 'number',
      rowItemKey: 'SHG',
    },
    {
      title: 'SHA',
      type: 'number',
      rowItemKey: 'SHA',
    },
    {
      title: 'HIT',
      type: 'number',
      rowItemKey: 'HIT',
    },
    {
      title: 'BKS',
      type: 'number',
      rowItemKey: 'BKS',
    },
    {
      title: 'TKA',
      type: 'number',
      rowItemKey: 'TKA',
    },
    {
      title: 'GVA',
      type: 'number',
      rowItemKey: 'GVA',
    },
    {
      title: 'S',
      type: 'number',
      rowItemKey: 'S',
    },
    {
      title: 'S%',
      type: 'number',
      rowItemKey: 'S%',
    },
    {
      title: 'TOI',
      type: 'number',
      rowItemKey: 'TOI',
    },
  ]

  const goalieColumns = [
    {
      title: 'Goalies',
      type: 'string',
      rowItemKey: 'NAME',
    },
    {
      title: 'SA',
      type: 'number',
      rowItemKey: 'SA',
    },
    {
      title: 'SV',
      type: 'number',
      rowItemKey: 'SV',
    },
    {
      title: 'SV%',
      type: 'number',
      rowItemKey: 'SV%',
    },
    {
      title: 'GA',
      type: 'number',
      rowItemKey: 'GA',
    },
    {
      title: 'GAA',
      type: 'number',
      rowItemKey: 'GAA',
    },
    {
      title: 'SHO',
      type: 'number',
      rowItemKey: 'SHO',
    },
    {
      title: 'MIN',
      type: 'number',
      rowItemKey: 'MIN',
    },
  ]

  const onClickSelectPlayer = (row: GameraGridRow) => () =>
    selectedId.set({
      id: row.NAME?.meta?.id ?? 0,
      alignment: teamKey,
    })

  $: defenderIds = Object.entries($players)
    .filter((x) => x[1]?.position === 'DEFENSEMAN')
    .map((x) => x[1]?.id)
  $: goalieIds = Object.entries($players)
    .filter((x) => x[1]?.position === 'GOALIE')
    .map((x) => x[1]?.id)
  $: forwardIds = Object.entries($players)
    .filter(
      (x) => x[1]?.position !== 'DEFENSEMAN' && x[1]?.position !== 'GOALIE',
    )
    .map((x) => x[1]?.id)

  $: splits = $playerStats[teamKey]?.splits?.[0].splits

  $: stats = $playerStats[teamKey]?.statsLookup

  $: forwardRows =
    splits
      ?.filter((s) => forwardIds.includes(s.playerId.toString()))
      .map((s) => {
        const player = $players[s.playerId]
        const statmodel = stats?.[s.statsLookupKey]

        if (player) {
          return {
            NAME: {
              display: player?.longName ?? '',
              value: player?.longName ?? '',
              meta: {
                id: Number(player?.id),
                shortDisplay: player?.shortName,
                position: player?.uniformNumber
                  ? `#${player.uniformNumber}`
                  : undefined,
              },
            },
            G: statmodel?.Goals ?? { display: '0', value: 0 },
            A: statmodel?.Assists ?? { display: '0', value: 0 },
            P: statmodel?.Points ?? { display: '0', value: 0 },
            '+/-': statmodel?.PlusMinus ?? { display: '0', value: 0 },
            PIM: statmodel?.['Penalty-Minutes'] ?? { display: '0', value: 0 },
            PPG: statmodel?.['PowerPlay-Goals'] ?? { display: '0', value: 0 },
            PPA: statmodel?.['PowerPlay-Assists'] ?? { display: '0', value: 0 },
            SHG: statmodel?.['ShortHanded-Goals'] ?? { display: '0', value: 0 },
            SHA: statmodel?.['ShortHanded-Assists'] ?? {
              display: '0',
              value: 0,
            },
            S: statmodel?.Shots ?? { display: '0', value: 0 },
            'S%': statmodel?.['ShootingPercentage'] ?? {
              display: '0.0',
              value: 0,
            },
            FOW: statmodel?.['Faceoff-Wins'] ?? { display: '0', value: 0 },
            FO: statmodel?.['Faceoff-Count'] ?? { display: '0', value: 0 },
            'FOW%': statmodel?.['Faceoff-WinPercentage'] ?? {
              display: '0.0',
              value: 0,
            },
            HIT: statmodel?.Hits ?? { display: '0', value: 0 },
            BKS: statmodel?.BlockedShots ?? { display: '0', value: 0 },
            TKA: statmodel?.Takeaways ?? { display: '0', value: 0 },
            GVA: statmodel?.Giveaways ?? { display: '0', value: 0 },
            TOI: statmodel?.TimeOnIce ?? { display: '0', value: 0 },
          }
        }
      })
      .filter((x) => !!x)
      .sort((a, b) => {
        if (a.P.value !== b.P.value) return b.P.value - a.P.value
        const aLastname =
          a.NAME.display.split(' ')[a.NAME.display.split(' ').length - 1]
        const bLastname =
          b.NAME.display.split(' ')[b.NAME.display.split(' ').length - 1]
        return aLastname.localeCompare(bLastname)
      }) ?? []

  $: defenderRows =
    splits
      ?.filter((s) => defenderIds.includes(s.playerId.toString()))
      .map((s) => {
        const player = $players[s.playerId]
        const statmodel = stats?.[s.statsLookupKey]

        if (player) {
          return {
            NAME: {
              display: player?.longName ?? '',
              value: player?.longName ?? '',
              meta: {
                id: Number(player?.id),
                shortDisplay: player?.shortName,
                position: player?.uniformNumber
                  ? `#${player.uniformNumber}`
                  : undefined,
              },
            },
            G: statmodel?.Goals ?? { display: '0', value: 0 },
            A: statmodel?.Assists ?? { display: '0', value: 0 },
            P: statmodel?.Points ?? { display: '0', value: 0 },
            '+/-': statmodel?.PlusMinus ?? { display: '0', value: 0 },
            PIM: statmodel?.['Penalty-Minutes'] ?? { display: '0', value: 0 },
            PPG: statmodel?.['PowerPlay-Goals'] ?? { display: '0', value: 0 },
            PPA: statmodel?.['PowerPlay-Assists'] ?? { display: '0', value: 0 },
            SHG: statmodel?.['ShortHanded-Goals'] ?? { display: '0', value: 0 },
            SHA: statmodel?.['ShortHanded-Assists'] ?? {
              display: '0',
              value: 0,
            },
            HIT: statmodel?.Hits ?? { display: '0', value: 0 },
            BKS: statmodel?.BlockedShots ?? { display: '0', value: 0 },
            TKA: statmodel?.Takeaways ?? { display: '0', value: 0 },
            GVA: statmodel?.Giveaways ?? { display: '0', value: 0 },
            S: statmodel?.Shots ?? { display: '0', value: 0 },
            'S%': statmodel?.['ShootingPercentage'] ?? {
              display: '0.0',
              value: 0,
            },
            TOI: statmodel?.TimeOnIce ?? { display: '0', value: 0 },
          }
        }
      })
      .filter((x) => !!x)
      .sort((a, b) => {
        if (a.P.value !== b.P.value) return b.P.value - a.P.value
        const aLastname =
          a.NAME.display.split(' ')[a.NAME.display.split(' ').length - 1]
        const bLastname =
          b.NAME.display.split(' ')[b.NAME.display.split(' ').length - 1]
        return aLastname.localeCompare(bLastname)
      }) ?? []

  $: goalieRows =
    splits
      ?.filter((s) => goalieIds.includes(s.playerId.toString()))
      .map((s) => {
        const player = $players[s.playerId]
        const statmodel = stats?.[s.statsLookupKey]

        if (player) {
          return {
            NAME: {
              display: player?.longName ?? '',
              value: player?.longName ?? '',
              meta: {
                id: Number(player?.id),
                shortDisplay: player?.shortName,
                position: player?.uniformNumber
                  ? `#${player.uniformNumber}`
                  : undefined,
              },
            },
            SA: statmodel?.ShotsAgainst ?? { display: '0', value: 0 },
            SV: statmodel?.Saves ?? { display: '0', value: 0 },
            'SV%': statmodel?.SavePercentage ?? { display: '0', value: 0 },
            GA: statmodel?.GoalsAgainst ?? { display: '0', value: 0 },
            GAA: statmodel?.GoalsAgainstAverage ?? { display: '0', value: 0 },
            SHO: statmodel?.Shutouts ?? { display: '0', value: 0 },
            MIN: statmodel?.MinutesPlayed ?? { display: '0', value: 0 },
          }
        }
      })
      .filter((x) => !!x)
      .sort((a, b) => b.MIN.value - a.MIN.value) ?? []

  $: highlightActivePlayers = live
    ? $plays
        .filter((p) => p.period === $periodClock.period)
        .findLast((p) => p[teamIdKey])
        ?.[teamIdKey]?.reduce(
          (acc, id) => {
            return {
              ...acc,
              [$players[id]?.longName ?? '']: team?.colors,
            }
          },
          {} as Record<string, Colors | undefined>,
        )
    : undefined
</script>

<Grid
  textInherit
  disableSort
  data={{ name: 'forward', rows: forwardRows, columns: forwardColumns }}
  highlight={highlightActivePlayers}
  stickyColumns={['NAME']}
  onRowClick={onClickSelectPlayer}
/>

<Grid
  textInherit
  disableSort
  data={{ name: 'defender', rows: defenderRows, columns: defenderColumns }}
  highlight={highlightActivePlayers}
  stickyColumns={['NAME']}
  onRowClick={onClickSelectPlayer}
/>

<Grid
  textInherit
  disableSort
  data={{ name: 'goalie', rows: goalieRows, columns: goalieColumns }}
  highlight={highlightActivePlayers}
  stickyColumns={['NAME']}
  onRowClick={onClickSelectPlayer}
/>

{#if live}
  <div class="flex items-center">
    <span
      class="w-4 h-4 mr-2"
      style:background={team?.colors?.backgroundColor}
    />
    On ice
  </div>
{/if}
